import { useEffect, useState } from 'react';
import { THEMES, type DigitStyle, type ThemeKey } from '../channels/scoreboard/themes';
import { ZoneRenderer } from './ZoneRenderer';
import { resolveLayoutTheme, WidgetContext } from './context';
import { useResolvedTemplate } from './useResolvedTemplate';
import { BESPOKE_DESIGNS, isBespoke } from './bespoke/registry';
import { EFFECT_COMPONENTS } from '../effects/effectComponents';
import type { GameEffect, GameEffectType } from '../effects/types';
import { deriveEffectTheme, fxVars } from '../effects/effectTheme';
import { EffectThemeProvider } from '../effects/effectThemeContext';

// Dev/preview harness for the widget engine.
//   /preview-engine?template=<built-in key>   — built-in template
//   /preview-engine?layout=<saved layout id>  — saved layout from builder
//   /preview-engine?...&effect=<GameEffectType> — overlay one effect on loop
// Renders with sample data at 16:9.
export function PreviewEngineScreen() {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('layout') ?? params.get('template') ?? undefined;
  const template = useResolvedTemplate(ref);
  const themeKey = (params.get('theme') as ThemeKey) ?? 'classic';
  const baseTheme = THEMES[themeKey] ?? THEMES.classic;
  const baseMode = (params.get('mode') as DigitStyle) ?? 'segment';
  const { theme, mode, font, frame } = resolveLayoutTheme(
    baseTheme,
    baseMode,
    template.theme,
  );

  // Effect theme for previewed effects — match what AnimationHost does in production.
  const effectThemeKey = template.theme?.preset ?? themeKey ?? 'classic';
  const effectTheme = deriveEffectTheme(theme, effectThemeKey, null);

  // Optional effect overlay: ?effect=<GameEffectType>. Only render if the type
  // has a registered component. Loops by remounting on a bumped key.
  const effectParam = params.get('effect') as GameEffectType | null;
  const EffectCmp =
    effectParam && effectParam in EFFECT_COMPONENTS
      ? EFFECT_COMPONENTS[effectParam]
      : undefined;
  const [loopKey, setLoopKey] = useState(0);
  const previewEffect: GameEffect | null = EffectCmp
    ? {
        id: `preview-${loopKey}`,
        type: effectParam as GameEffectType,
        side: 'home',
        playerId: undefined,
        data: { run: 8, milestone: '20_POINTS', period: 1, home: 'WILDCATS', away: 'HAWKS' },
        at: new Date().toISOString(),
      }
    : null;

  // On effect completion, wait ~600ms then bump the key to replay (remount).
  const [pendingReplay, setPendingReplay] = useState(false);
  useEffect(() => {
    if (!pendingReplay) return;
    const t = window.setTimeout(() => {
      setPendingReplay(false);
      setLoopKey((k) => k + 1);
    }, 600);
    return () => window.clearTimeout(t);
  }, [pendingReplay]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ position: 'relative', width: '100vw', aspectRatio: '16 / 9', maxHeight: '100vh' }}>
        {isBespoke(template.id) ? (
          <WidgetContext.Provider
            value={{
              snapshot: null,
              state: null,
              theme,
              mode,
              font,
              frame,
              isSample: true,
            }}
          >
            {(() => { const Design = BESPOKE_DESIGNS[template.id]; return <Design />; })()}
          </WidgetContext.Provider>
        ) : (
          <ZoneRenderer
            template={template}
            data={{
              snapshot: null,
              state: null,
              theme,
              mode,
              font,
              frame,
              isSample: true,
            }}
          />
        )}
        {EffectCmp && previewEffect && (
          <EffectThemeProvider value={effectTheme}>
            <div style={{ ...fxVars(effectTheme), position: 'absolute', inset: 0, zIndex: 1000, pointerEvents: 'none' }}>
              <EffectCmp
                key={loopKey}
                effect={previewEffect}
                snapshot={null}
                onDone={() => setPendingReplay(true)}
              />
            </div>
          </EffectThemeProvider>
        )}
      </div>
    </div>
  );
}
